/**
 * "All Public Users" organisation: Templates & Macros only (Jacky 2026-11-03).
 *
 * Users working in this organisation must not open the CRE report window —
 * not from the Templates page ("Create Report"), not from a link, and not
 * after signing in at cre.medilis.ai directly (they land on Templates &
 * Macros). They are meant to use the MedMic extension.
 *
 * The organisation is identified by its fixed id (the same constant the MedMic
 * extension uses: PUBLIC_USERS_ORG_ID), never by its name.
 *
 * The MedMic desktop app (CRE Agent Mode, real `window.creAgentHost` bridge,
 * not just `?agent=1` in the URL) keeps the report page: its Process AI runs
 * there.
 *
 * Frontend only: the report APIs themselves are protected by the backend.
 */
import { AUTH_SOURCE } from "../constants/authSource";
import { getSelectedCreOrganisationId, getCreOrganisations } from "./creOrganisation";
import { getAgentHost } from "./agentMode/agentBridge";

export const PUBLIC_USERS_ORG_ID = "00000000-0000-0000-0000-000000000000";

function normalizeOrgId(id) {
  return String(id ?? "")
    .trim()
    .replace(/^\{|\}$/g, "")
    .toLowerCase();
}

/** @param {string | null | undefined} organisationId */
export function isPublicUsersOrganisationId(organisationId) {
  return !!organisationId && normalizeOrgId(organisationId) === PUBLIC_USERS_ORG_ID;
}

/**
 * @param {{ authSource?: string | null, creUser?: object | null, creSelectedOrganisationId?: string | null, inMedMicDesktop?: boolean }} p
 * @returns {boolean} true when the report window must not be shown
 */
export function isCreReportBlocked({ authSource, creUser, creSelectedOrganisationId, inMedMicDesktop = false }) {
  if (authSource !== AUTH_SOURCE.CRE) return false;
  if (inMedMicDesktop) return false;
  const orgId = getSelectedCreOrganisationId(creUser, creSelectedOrganisationId);
  return isPublicUsersOrganisationId(orgId);
}

/** Other organisations the user could switch to (to get the report window back). */
export function otherCreOrganisations(creUser) {
  return getCreOrganisations(creUser).filter((o) => !isPublicUsersOrganisationId(o.organisationId));
}

/** True inside the MedMic desktop app (its preload provides the bridge host). */
export function isInMedMicDesktop(win = typeof window !== "undefined" ? window : undefined) {
  return !!getAgentHost(win);
}
